/**
 * 屏幕 0：主屏（5 元素竖条方块 + 像素形象 + 输入框）
 */

import { createSnow } from "../snow";
import { createDrift, type DriftController } from "../drift";
import { isValidName, isNameLike, analyze } from "../charAnalyzer";
import { getNamePool, addName } from "../nameHistory";
import { generateMonsters } from "../api";
import { generateLocalMonster } from "../localMonster";
import { addMonsters, getCodexCount } from "../codex";
import { getZoo } from "../zoo";
import { ELEMENTS } from "../elements";
import { drawElementToken, TOKEN_PALETTES, type ElementToken } from "../elementTokens";
import seedPool from "../../data/nameSeedPool.json";
import type { AnyGenerateResponse, CharAnalysis, MonsterDesc } from "../../shared/types";

export interface Screen0Deps {
  root: HTMLElement;
  fontFamily: string;
  onMonsters: (name: string, analysis: CharAnalysis, monsters: MonsterDesc[]) => void;
  onError: (msg: string) => void;
  onGoZoo?: () => void;
  onGoBattle?: () => void;
}

export interface Screen0Controller {
  destroy: () => void;
  setBusy: (busy: boolean) => void;
}

export function createScreen0(deps: Screen0Deps): Screen0Controller {
  const { root, fontFamily, onMonsters, onError, onGoZoo, onGoBattle } = deps;

  const destroySnow = createSnow(root);

  const seedNames = (seedPool as { names: string[] }).names;
  const namePool = getNamePool(seedNames);
  const drift: DriftController = createDrift(root, fontFamily, {
    nameCount: 10,
    blockCount: 14,
    speed: 50,
    namePool: namePool.length > 0 ? namePool : seedNames.slice(0, 30),
  });

  const screen = document.createElement("div");
  screen.className = "screen-0";

  // === 主视觉：5 元素竖条（灵符，无文字） ===
  const bars = document.createElement("div");
  bars.className = "element-bars";

  ELEMENTS.forEach((el) => {
    const bar = document.createElement("div");
    bar.className = `element-bar bar-${el}`;

    // 中部：灵符画布
    const canvas = document.createElement("canvas");
    canvas.width = 64;
    canvas.height = 64;
    canvas.className = "bar-canvas";
    bar.appendChild(canvas);

    bars.appendChild(bar);

    // 渲染对应元素的灵符（一次画完 64×64）
    const ctx = canvas.getContext("2d")!;
    ctx.imageSmoothingEnabled = false;
    drawElementToken(el as ElementToken, ctx, TOKEN_PALETTES[el as ElementToken]);
  });

  screen.appendChild(bars);

  // === 输入框（底部紧凑） ===
  const inputWrap = document.createElement("div");
  inputWrap.className = "input-wrap";

  const promptEl = document.createElement("span");
  promptEl.className = "prompt";
  promptEl.textContent = ">";

  const input = document.createElement("input");
  input.type = "text";
  input.maxLength = 8;
  input.spellcheck = false;
  input.autocomplete = "off";
  input.autocapitalize = "off";
  input.placeholder = "输入名字";

  const submitBtn = document.createElement("button");
  submitBtn.className = "submit-btn";
  submitBtn.textContent = "五行分析";

  inputWrap.appendChild(promptEl);
  inputWrap.appendChild(input);
  inputWrap.appendChild(submitBtn);

  // === 底部导航 ===
  const navBar = document.createElement("div");
  navBar.className = "screen0-nav";

  const codexHint = document.createElement("div");
  codexHint.className = "codex-count";
  codexHint.innerHTML = `五行阶级 · <b>${getZoo().length}</b> 人`;
  navBar.appendChild(codexHint);

  if (onGoZoo) {
    const zooBtn = document.createElement("button");
    zooBtn.className = "nav-btn screen0-nav-btn";
    zooBtn.innerHTML = `◆ 五行阶级`;
    zooBtn.addEventListener("click", () => onGoZoo());
    navBar.appendChild(zooBtn);
  }
  // 系统对战入口已内置到"五行阶级"页面（点击人偶发起对波），不再单独展示按钮

  // 全部包成底部 stack
  const bottom = document.createElement("div");
  bottom.className = "screen0-bottom";
  bottom.appendChild(inputWrap);
  bottom.appendChild(navBar);

  screen.appendChild(bottom);

  root.appendChild(screen);

  setTimeout(() => input.focus(), 100);

  let busy = false;
  const setBusy = (b: boolean) => {
    busy = b;
    input.disabled = b;
    submitBtn.disabled = b;
    if (b) {
      inputWrap.classList.remove("error");
    }
  };

  /**
   * 顶部飘窗提示：用于"未输入名字"等空状态
   *  - 顶部居中显示一条小框
   *  - 2 秒内向上飘移 + 渐淡消失
   *  - 多次触发时叠加显示，不打断
   */
  const showTopToast = (msg: string) => {
    const toast = document.createElement("div");
    toast.className = "screen0-toast";
    toast.textContent = msg;
    // 写入 screen 容器内（screen0 屏整体定位）
    screen.appendChild(toast);
    // 下一帧加 .show 触发动画
    requestAnimationFrame(() => {
      toast.classList.add("show");
    });
    // 3 秒后移除（与 CSS 动画时长同步）
    setTimeout(() => {
      toast.classList.add("hide");
      setTimeout(() => {
        try { toast.remove(); } catch { /* ignore */ }
      }, 500);
    }, 3000);
  };

  const handleSubmit = async () => {
    if (busy) return;
    const name = input.value.trim();
    // 空名或不规范：统一走顶部飘窗（不弹输入框下候选字 / 红字）
    if (name.length === 0 || !isValidName(name)) {
      showTopToast("输入不规范");
      input.focus();
      return;
    }
    setBusy(true);
    const analysis = analyze(name);
    addName(name);
    drift.appendName(name);

    // 尝试 API；失败/超时时本地兜底（保证屏 2 一定能进）
    const result: AnyGenerateResponse = await generateMonsters({ name, charAnalysis: analysis });
    let monsters: MonsterDesc[];
    if (result.ok) {
      monsters = result.monsters;
    } else {
      // 本地兜底生成
      const local = generateLocalMonster(analysis, isNameLike(name));
      monsters = [local];
    }
    addMonsters(monsters);
    codexHint.innerHTML = `已解锁 <b>${getCodexCount()}</b> 只 · 圈养 <b>${getZoo().length}</b>/10`;
    onMonsters(name, analysis, monsters);
  };

  input.addEventListener("keydown", (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleSubmit();
    }
  });
  input.addEventListener("input", () => {
    inputWrap.classList.remove("error");
  });
  submitBtn.addEventListener("click", () => handleSubmit());

  const destroy = () => {
    destroySnow();
    drift.destroy();
    screen.remove();
  };

  return { destroy, setBusy };
}